import React, { useEffect, useState } from 'react';
import { KeyValuePair, SelectOpt } from '../CommonTypes';
import { TitleApi } from '../../../api/TitleApi';
import SelectInput from '../SelectInput/SelectInput';
import { useTranslation } from 'react-i18next';

/**
 * `TitleSelectInput` loads available title options (e.g. Mr/Ms/Mrs) from the
 * backend and renders them using `SelectInput`.
 *
 * Behaviour:
 * - Fetches titles on mount via `TitleApi.getTitlesAsync(countryCode, token, ...)`.
 * - Maps the API response (`KeyValuePair[]`) into `SelectOpt[]` for `SelectInput`.
 * - Uses i18n keys for the default/placeholder and loading text.
 */

type TitleSelectInputProps = {
  value: string;
  error: string;
  onSelectedValueChange: (value: string) => void;
  token: string;
  countryCode: string;
};

const TitleSelectInput = ({
  value,
  error,
  onSelectedValueChange,
  token,
  countryCode
}: TitleSelectInputProps): JSX.Element => {
  const [titles, setTitles] = useState<SelectOpt[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    const loadTitles = async () => {
      const controller = new AbortController();

      const setTitleSuccess = (newTitles: KeyValuePair[]) => {
        const titleOptions: SelectOpt[] = [];

        newTitles.forEach((item, index) => {
          const lookupOption: SelectOpt = {
            id: (index + 1).toString(),
            text: item.value,
            value: item.key,
            disabled: false
          };
          titleOptions.push(lookupOption);
        });

        setTitles(titleOptions);
      };

      const setTitleFailure = (err: string) => {
        console.log(`Get title error: ${err}`);
      };

      await new TitleApi().getTitlesAsync(
        countryCode,
        token,
        setTitleSuccess,
        setTitleFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    };

    loadTitles();
  }, []);

  return (
    <SelectInput
      name='title'
      className='input-text w250'
      value={value}
      options={titles}
      onChange={onSelectedValueChange}
      defaultText={t('personal-details.title-default')}
      forceDefault
      addDefaultOption
      error={error}
      loadingText={t('loading-placeholder.loading-text')}
    />
  );
};

export default TitleSelectInput;
